import React, { useState, useEffect } from 'react'
import { X, Check, ChevronRight, ChevronLeft, Calculator } from 'lucide-react'

// Package configurations from PRD v3 + v9 Catering Trays
const CATERING_TRAYS = {
  small: {
    id: 'cateringTraysSmall',
    name: 'Catering Trays',
    pricePerTail: 20,
    minTails: 12,
    maxTails: 20,
    baseFee: 50,
    baseFeeLabel: 'Delivery Fee'
  },
  large: {
    id: 'cateringTraysLarge',
    name: 'Catering Trays',
    pricePerTail: 19,
    minTails: 21,
    maxTails: 79,
    baseFee: 50,
    baseFeeLabel: 'Delivery Fee'
  }
}

const PACKAGES = {
  partyStarter: {
    id: 'partyStarter',
    name: 'The Party Starter',
    pricePerTail: 16,
    minTails: 80,
    maxTails: 120,
  },
  corporateFavorite: {
    id: 'corporateFavorite',
    name: 'The Corporate Favorite',
    pricePerTail: 15,
    minTails: 140,
    maxTails: 240,
  },
  mainEvent: {
    id: 'mainEvent',
    name: 'The Main Event',
    pricePerTail: 14,
    minTails: 250,
    maxTails: 400,
  }
}

// Labor calculation lookup table from PRD
const LABOR_TABLE = [
  { tails: 80, hours: 0.58, cost: 58.33 },
  { tails: 100, hours: 0.75, cost: 75.00 },
  { tails: 120, hours: 0.92, cost: 91.67 },
  { tails: 140, hours: 1.00, cost: 100.00 },
  { tails: 160, hours: 1.17, cost: 116.67 },
  { tails: 180, hours: 1.25, cost: 125.00 },
  { tails: 200, hours: 1.42, cost: 141.67 },
  { tails: 220, hours: 1.58, cost: 158.33 },
  { tails: 240, hours: 1.67, cost: 166.67 },
  { tails: 260, hours: 1.83, cost: 183.33 },
  { tails: 280, hours: 2.00, cost: 200.00 },
  { tails: 300, hours: 2.08, cost: 208.33 },
  { tails: 320, hours: 2.25, cost: 225.00 },
  { tails: 340, hours: 2.42, cost: 241.67 },
  { tails: 360, hours: 2.50, cost: 250.00 },
  { tails: 380, hours: 2.67, cost: 266.67 },
  { tails: 400, hours: 2.83, cost: 283.33 }
]

// Serving size presets - v8 UX Redesign (ordered smallest to largest)
const SERVING_SIZES = [
  { value: 0.2, label: 'Light', description: '1 tail per 5 guests', id: 'light' },
  { value: 0.33, label: 'Standard', description: '1 tail per 3 guests', id: 'standard', isPopular: true },
  { value: 0.5, label: 'Hearty', description: '1 tail per 2 guests', id: 'hearty' },
  { value: 1.0, label: 'Generous', description: '1 tail per guest', id: 'generous' }
]

const MOBILE_KITCHEN_FEE = 150
const DELIVERY_FEE = 50
const CHAFING_FEE = 30
const SERVER_RATE = 60
const STAFF_RATE = 60 // Same rate for event staff
const TAX_RATE = 0.095

const CateringWizard = ({ isOpen, onClose }) => {
  // Guest-first flow state
  const [step, setStep] = useState(1)
  const [guestCount, setGuestCount] = useState(240)
  const [servingSize, setServingSize] = useState(0.33)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [numTails, setNumTails] = useState(0)
  const [serversEnabled, setServersEnabled] = useState(false)
  const [numServers, setNumServers] = useState(1)
  const [serverHours, setServerHours] = useState(2.5)
  const [contactInfo, setContactInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    eventDate: '',
    eventLocation: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [customQuoteRequired, setCustomQuoteRequired] = useState(false)
  const [validationState, setValidationState] = useState('default')
  const [validationMessage, setValidationMessage] = useState('')
  const [canContinue, setCanContinue] = useState(true)
  const [configMode, setConfigMode] = useState('serving') // 'serving' or 'tails'
  const [directTailInput, setDirectTailInput] = useState('')

  // v9 Catering Trays state
  const [serviceType, setServiceType] = useState('foodTruck') // 'cateringTrays' or 'foodTruck'
  const [deliveryMethod, setDeliveryMethod] = useState('delivery') // 'delivery' or 'pickup'
  const [disabledTooltip, setDisabledTooltip] = useState(null) // Track which disabled option is showing tooltip
  const [chafingEnabled, setChafingEnabled] = useState(false)
  const [staffEnabled, setStaffEnabled] = useState(false)
  const [numStaff, setNumStaff] = useState(1)
  const [staffHours, setStaffHours] = useState(2.5)

  // v9: Determine service type and package based on tail count
  const determineServiceAndPackage = (tails) => {
    if (tails >= 12 && tails <= 20) {
      setServiceType('cateringTrays')
      return 'cateringTraysSmall'
    } else if (tails >= 21 && tails <= 79) {
      setServiceType('cateringTrays')
      return 'cateringTraysLarge'
    } else if (tails >= 80 && tails <= 120) {
      setServiceType('foodTruck')
      return 'partyStarter'
    } else if (tails >= 140 && tails <= 240) {
      setServiceType('foodTruck')
      return 'corporateFavorite'
    } else if (tails >= 250 && tails <= 400) {
      setServiceType('foodTruck')
      return 'mainEvent'
    } else {
      setServiceType('foodTruck')
      return null // Out of range - custom quote required
    }
  }

  // Calculate tails and auto-select package whenever guestCount or servingSize changes
  useEffect(() => {
    const parsedGuests = parseInt(guestCount, 10)
    if (isNaN(parsedGuests) || parsedGuests <= 0) {
      setNumTails(0)
      setSelectedPackage(null)
      setCustomQuoteRequired(false)
      return
    }

    const calculatedTails = Math.ceil(parsedGuests * servingSize)
    setNumTails(calculatedTails)

    const pkg = determineServiceAndPackage(calculatedTails)
    setSelectedPackage(pkg)
    setCustomQuoteRequired(pkg === null)
  }, [guestCount, servingSize])

  // v9.3: Auto-selection logic - only triggers when exactly one serving size option is valid
  useEffect(() => {
    // Only apply auto-selection in Step 2 and serving mode
    if (step !== 2 || configMode !== 'serving') return

    const parsedGuests = parseInt(guestCount, 10)
    if (isNaN(parsedGuests) || parsedGuests <= 0) return

    // Calculate which serving sizes are valid (not disabled)
    const validOptions = SERVING_SIZES.filter(option => {
      const calculatedTails = Math.ceil(parsedGuests * option.value)
      return calculatedTails >= 12 && calculatedTails <= 400
    })

    // Only auto-select if exactly one option is valid
    if (validOptions.length === 1) {
      setServingSize(validOptions[0].value)
    }
  }, [guestCount, step, configMode])

  // Calculate labor cost with linear interpolation
  const calculateLaborCost = (tails) => {
    if (!tails || tails < 80 || tails > 400) return { hours: 0, cost: 0 }

    // Find exact match
    const exactMatch = LABOR_TABLE.find(entry => entry.tails === tails)
    if (exactMatch) return { hours: exactMatch.hours, cost: exactMatch.cost }

    // Find surrounding values in table
    let lower = LABOR_TABLE[0]
    let upper = LABOR_TABLE[LABOR_TABLE.length - 1]

    for (let i = 0; i < LABOR_TABLE.length - 1; i++) {
      if (tails >= LABOR_TABLE[i].tails && tails <= LABOR_TABLE[i + 1].tails) {
        lower = LABOR_TABLE[i]
        upper = LABOR_TABLE[i + 1]
        break
      }
    }

    // Linear interpolation
    const ratio = (tails - lower.tails) / (upper.tails - lower.tails)
    const hours = lower.hours + ratio * (upper.hours - lower.hours)
    const cost = lower.cost + ratio * (upper.cost - lower.cost)

    return { hours: Number(hours.toFixed(2)), cost: Number(cost.toFixed(2)) }
  }

  // v9: Calculate all costs (with Catering Trays support)
  const calculateCosts = () => {
    if (!selectedPackage || !numTails) {
      return {
        tailCost: 0,
        baseFee: 0,
        baseFeeLabel: '',
        laborCost: 0,
        laborHours: 0,
        serversCost: 0,
        chafingCost: 0,
        staffCost: 0,
        subtotal: 0,
        estimatedTax: 0,
        totalCost: 0
      }
    }

    // Determine if this is Catering Trays or Food Truck
    let pkg, baseFee, baseFeeLabel, tailCost, laborCost = 0, laborHours = 0

    if (serviceType === 'cateringTrays') {
      // Catering Trays pricing
      pkg = CATERING_TRAYS[selectedPackage === 'cateringTraysSmall' ? 'small' : 'large']
      tailCost = numTails * pkg.pricePerTail
      baseFee = deliveryMethod === 'delivery' ? pkg.baseFee : 0
      baseFeeLabel = deliveryMethod === 'delivery' ? pkg.baseFeeLabel : 'Pickup (Free)'
    } else {
      // Food Truck pricing
      pkg = PACKAGES[selectedPackage]
      tailCost = numTails * pkg.pricePerTail
      baseFee = MOBILE_KITCHEN_FEE
      baseFeeLabel = 'Mobile Kitchen Fee'
      const labor = calculateLaborCost(numTails)
      laborCost = labor.cost
      laborHours = labor.hours
    }

    // Add-ons
    const serversCost = serversEnabled ? numServers * serverHours * SERVER_RATE : 0
    const chafingCost = chafingEnabled ? CHAFING_FEE : 0
    const staffCost = staffEnabled ? numStaff * staffHours * STAFF_RATE : 0

    const subtotal = tailCost + baseFee + laborCost + serversCost + chafingCost + staffCost
    const estimatedTax = subtotal * TAX_RATE
    const totalCost = subtotal + estimatedTax

    return {
      tailCost: Number(tailCost.toFixed(2)),
      baseFee: Number(baseFee.toFixed(2)),
      baseFeeLabel,
      laborCost: Number(laborCost.toFixed(2)),
      laborHours,
      serversCost: Number(serversCost.toFixed(2)),
      chafingCost: Number(chafingCost.toFixed(2)),
      staffCost: Number(staffCost.toFixed(2)),
      subtotal: Number(subtotal.toFixed(2)),
      estimatedTax: Number(estimatedTax.toFixed(2)),
      totalCost: Number(totalCost.toFixed(2))
    }
  }

  const costs = calculateCosts()

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    // v9: Handle both Catering Trays and Food Truck packages
    const pkg = serviceType === 'cateringTrays'
      ? CATERING_TRAYS[selectedPackage === 'cateringTraysSmall' ? 'small' : 'large']
      : PACKAGES[selectedPackage]

    const payload = {
      contactInfo,
      serviceType,
      configuration: {
        packageName: pkg.name,
        pricePerTail: pkg.pricePerTail,
        guestCount,
        servingSize: configMode === 'serving' ? servingSize : null,
        servingSizeDescription: configMode === 'serving' ? getServingSizeLabel(servingSize) : 'Direct Tail Count',
        numTails,
        configurationMode: configMode,
        laborHours: costs.laborHours,
        serversEnabled: serviceType === 'foodTruck' ? serversEnabled : false,
        numServers: serversEnabled ? numServers : 0,
        serverHours: serversEnabled ? serverHours : 0
      },
      addOns: serviceType === 'cateringTrays' ? {
        deliveryMethod,
        chafingEnabled,
        staffEnabled,
        numStaff: staffEnabled ? numStaff : 0,
        staffHours: staffEnabled ? staffHours : 0
      } : null,
      costs,
      timestamp: new Date().toISOString(),
      source: 'catering_wizard_v9'
    }

    try {
      const response = await fetch('/.netlify/functions/catering-wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitSuccess(true)
      } else {
        setSubmitError('Failed to submit your configuration. Please try again.')
        console.error('Failed to send:', result.error)
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again or contact us directly at catering@tailtations.com')
      console.error('Error submitting:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Reset wizard state (Bug #2 fix)
  const resetWizardState = () => {
    setStep(1)
    setGuestCount(240)
    setServingSize(0.33)
    setSelectedPackage(null)
    setNumTails(0)
    setServersEnabled(false)
    setNumServers(1)
    setServerHours(2.5)
    setContactInfo({
      fullName: '',
      email: '',
      phone: '',
      eventDate: '',
      eventLocation: ''
    })
    setSubmitSuccess(false)
    setSubmitError(null)
    setCustomQuoteRequired(false)
    setValidationState('success')
    setValidationMessage('✅ Great! 240 guests is a perfect number for our standard serving size.')
    setCanContinue(true)
    setConfigMode('serving')
    setDirectTailInput('')
  }

  const handleClose = () => {
    resetWizardState()
    onClose()
  }

  // v9: Real-time validation for guest count (updated for 12-79 tail Catering Trays)
  const validateGuestCount = (count) => {
    const guests = parseInt(count, 10)

    if (isNaN(guests) || guests === 0) {
      setValidationState('default')
      setValidationMessage('')
      setCanContinue(false)
      return
    }

    // Calculate tails for all serving sizes
    const calculations = {
      light: Math.ceil(guests * 0.2),
      standard: Math.ceil(guests * 0.33),
      hearty: Math.ceil(guests * 0.5),
      main: Math.ceil(guests * 1.0),
    }

    // v9: Check if any serving size falls in Catering Trays range (12-79)
    const hasCateringTraysOption =
      (calculations.light >= 12 && calculations.light <= 79) ||
      (calculations.standard >= 12 && calculations.standard <= 79) ||
      (calculations.hearty >= 12 && calculations.hearty <= 79) ||
      (calculations.main >= 12 && calculations.main <= 79)

    // Smart validation logic - check if ANY serving size produces valid tails
    const hasValidServingSize = calculations.main >= 12 && calculations.main <= 400

    if (calculations.main < 12) {
      // Fixed: Clear static message for small groups
      setValidationState('suggestion')
      setValidationMessage(
        `💡 Tip: Our minimum order is 12 tails. For a group of this size, this provides plenty of servings. You can customize the exact amount in the next step.`
      )
      setCanContinue(true) // Non-blocking
    } else if (calculations.light > 400) {
      // Very large event - guide to Tail Count mode
      setValidationState('suggestion')
      setValidationMessage(`💡 Tip: For very large events, you can specify an exact tail count in the next step (up to 400 tails maximum).`)
      setCanContinue(true)
    } else if (hasValidServingSize) {
      // Valid guest count - any valid serving size works
      setValidationState('success')
      setValidationMessage(`✅ Perfect! We have you down for ${guests} guests. Continue to configure your serving size.`)
      setCanContinue(true)
    } else {
      // Fallback - shouldn't reach here but ensure Continue is enabled
      setValidationState('success')
      setValidationMessage(`✅ Perfect! We have you down for ${guests} guests.`)
      setCanContinue(true)
    }
  }

  // Bug #1 fix: Proper number input handling (v4)
  const handleGuestCountChange = (e) => {
    const value = e.target.value
    if (value === '') {
      setGuestCount('')
      validateGuestCount('')
      return
    }
    // Directly set the value - browser handles replacement
    setGuestCount(value)
    validateGuestCount(value)
  }

  const getServingSizeLabel = (size) => {
    const option = SERVING_SIZES.find(s => s.value === size)
    return option?.label || 'Custom'
  }

  const getServingSizeDescription = (size) => {
    const option = SERVING_SIZES.find(s => s.value === size)
    return option?.description || ''
  }

  // Get slider position based on selected serving size (v8)
  const getSliderPosition = (size) => {
    const index = SERVING_SIZES.findIndex(s => s.value === size)
    if (index === -1) return 33 // Default to middle
    // Map index (0-3) to percentage (0-100)
    return (index / (SERVING_SIZES.length - 1)) * 100
  }

  // v9: Tail count validation for direct input mode (updated for 12 minimum)
  const validateTailCount = (tails) => {
    const numTails = parseInt(tails, 10)

    if (isNaN(numTails) || tails === '') {
      setValidationState('default')
      setValidationMessage('')
      setCanContinue(false)
      return
    }

    if (numTails < 12) {
      setValidationState('error')
      setValidationMessage('Minimum order is 12 tails. Please enter a number between 12 and 400.')
      setCanContinue(false)
      return
    }

    if (numTails >= 12 && numTails <= 79) {
      setValidationState('success')
      setValidationMessage(`✅ Great! ${numTails} tails qualifies for <strong>Catering Trays</strong> delivery (no truck required).`)
      setCanContinue(true)
      return
    }

    if (numTails > 400) {
      setValidationState('error')
      setValidationMessage('Maximum online order is 400 tails. For larger events, please contact us at catering@tailtations.com')
      setCanContinue(false)
      return
    }

    // Valid tail count
    setValidationState('success')
    setValidationMessage(`✅ Great! ${numTails} tails is a perfect order.`)
    setCanContinue(true)
  }

  // Handler for direct tail input (v8)
  const handleDirectTailChange = (e) => {
    const value = e.target.value
    setDirectTailInput(value)
    validateTailCount(value)

    // Update numTails for cost calculations
    const parsed = parseInt(value, 10)
    if (!isNaN(parsed)) {
      setNumTails(parsed)
      const pkg = determineServiceAndPackage(parsed)
      setSelectedPackage(pkg)
      setCustomQuoteRequired(pkg === null)
    }
  }

  // v9: Toggle between serving size and tail count modes (updated default to 12)
  const toggleConfigMode = () => {
    if (configMode === 'serving') {
      // Switch to tail count mode - pre-fill with current numTails or minimum 12
      setConfigMode('tails')
      const defaultTails = numTails > 0 ? numTails.toString() : '12'
      setDirectTailInput(defaultTails)
      validateTailCount(defaultTails)
    } else {
      // Switch back to serving size mode
      setConfigMode('serving')
      setValidationState('success')
      setValidationMessage('')
      setCanContinue(true)
    }
  }

  if (!isOpen) return null

  // Success screen
  if (submitSuccess) {
    const pkg = selectedPackage ? PACKAGES[selectedPackage] : null
    return (
      <div className="catering-wizard fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'League Spartan, sans-serif' }}>
              Your Configuration Has Been Submitted!
            </h2>
            <p className="text-gray-600 mb-8">
              Thank you for using our catering wizard. Our catering specialist will contact you within 24 hours with a detailed quote.
            </p>

            {pkg && (
              <div className="bg-gray-50 rounded-lg p-6 text-left mb-8">
                <h3 className="font-bold text-lg mb-4">Configuration Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Guest Count:</span>
                    <span className="font-semibold">{guestCount} guests</span>
                  </div>
                  {configMode === 'serving' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Serving Size:</span>
                      <span className="font-semibold">{getServingSizeLabel(servingSize)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tails:</span>
                    <span className="font-semibold">{numTails} tails</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Package:</span>
                    <span className="font-semibold">{pkg.name}</span>
                  </div>
                  {serversEnabled && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Servers:</span>
                      <span className="font-semibold">{numServers} × {serverHours} hours</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                    <span className="text-gray-900 font-bold">Total Estimated Cost:</span>
                    <span className="font-bold text-xl !text-[#962831]">${costs.totalCost.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleClose}
              className="bg-[#962831] text-white px-8 py-3 rounded-lg hover:bg-[#962831]/90 transition-colors font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="catering-wizard fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Progress indicator */}
        <div className="bg-gray-50 py-6 border-b pl-4 sm:pl-8 pr-14 sm:pr-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {[
              { num: 1, label: 'Guests' },
              { num: 2, label: 'Configure' },
              { num: 3, label: 'Add-Ons' },
              { num: 4, label: 'Submit' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                      step > s.num
                        ? 'bg-[#962831] !text-white'
                        : step === s.num
                        ? 'bg-[#962831] !text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="text-xs mt-1 font-medium">{s.label}</span>
                </div>
                {idx < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded transition-colors ${
                      step > s.num ? 'bg-[#962831]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Left column - Configuration steps */}
          <div className="flex-1 px-4 md:px-8 pt-8 lg:pr-4">
            {/* Step 1: Guest Count Input */}
            {step === 1 && (
              <div className="pb-12">
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  How Many Guests?
                </h2>
                <p className="text-gray-600 mb-6">Start by telling us your estimated guest count.</p>

                <div className="mb-8">
                  <label className="block text-sm font-semibold mb-4">Estimated Guest Count *</label>
                  <input
                    type="number"
                    min="1"
                    max="5000"
                    value={guestCount}
                    onChange={handleGuestCountChange}
                    className="w-full px-6 py-4 text-2xl border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#962831] focus:border-[#962831]"
                    placeholder="Enter number of guests"
                  />

                  {/* Validation Message */}
                  {validationMessage && (
                    <div className={`mt-4 p-4 rounded-lg border-l-4 ${
                      validationState === 'error'
                        ? 'bg-red-50 border-red-500 text-red-900'
                        : validationState === 'suggestion'
                        ? 'bg-blue-50 border-blue-500 text-blue-900'
                        : 'bg-green-50 border-green-500 text-green-900'
                    }`}>
                      <p className="text-sm font-medium" dangerouslySetInnerHTML={{ __html: validationMessage }}></p>
                    </div>
                  )}

                  {!validationMessage && (
                    <p className="text-sm text-gray-500 mt-2">Enter the approximate number of guests attending your event</p>
                  )}
                </div>

                {validationState === 'success' && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6">
                    <h3 className="font-bold text-blue-900 mb-2">What to expect next:</h3>
                    <p className="text-sm text-blue-800">
                      We'll help you choose the right serving size and automatically calculate the number of tails needed for your event.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleNext}
                    disabled={!canContinue}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#962831] text-white px-6 py-3 rounded-lg hover:bg-[#962831]/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Hybrid Configuration (Serving Size OR Tail Count) */}
            {step === 2 && (
              <div className="pb-12">
                {/* Serving Size Mode */}
                {configMode === 'serving' && (
                  <>
                    <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                      Choose Your Serving Size
                    </h2>
                    <p className="text-gray-600 mb-6">Select how many lobster tails per guest. We'll calculate the total for you.</p>

                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Your Guest Count</span>
                        <span className="text-2xl font-bold !text-[#962831]">
                          {guestCount} guests
                        </span>
                      </div>
                    </div>

                    <div className="mb-8">
                      <label className="block text-sm font-semibold mb-4">
                        Serving Size per Guest: {getServingSizeLabel(servingSize)}
                      </label>

                      {/* Visual-only slider */}
                      <div className="relative w-full h-3 bg-gray-200 rounded-lg mb-6">
                        <div
                          className="absolute top-0 h-3 bg-[#962831] rounded-lg transition-all duration-300"
                          style={{ width: `${getSliderPosition(servingSize)}%` }}
                        />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-[#962831] rounded-full border-2 border-white shadow-lg transition-all duration-300"
                          style={{ left: `${getSliderPosition(servingSize)}%`, transform: 'translate(-50%, -50%)' }}
                        />
                      </div>

                      {/* v9.1: Preset serving size buttons with disabled state logic and improved spacing */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {SERVING_SIZES.map((option) => {
                          const calculatedTails = Math.ceil(guestCount * option.value)
                          const isDisabled = calculatedTails < 12 || calculatedTails > 400
                          const isBelowMin = calculatedTails < 12
                          const isAboveMax = calculatedTails > 400
                          const showTooltip = disabledTooltip === option.id

                          return (
                            <div key={option.id} className="relative">
                              <button
                                onClick={() => {
                                  if (isDisabled) {
                                    setDisabledTooltip(showTooltip ? null : option.id)
                                  } else {
                                    setServingSize(option.value)
                                  }
                                }}
                                className={`w-full p-4 rounded-lg border-2 transition-all text-center relative group ${
                                  isDisabled
                                    ? 'opacity-40 cursor-pointer border-dashed border-gray-300 bg-gray-50'
                                    : servingSize === option.value
                                      ? 'border-[#962831] bg-[#962831]/5'
                                      : 'border-gray-200 hover:border-[#962831]/50'
                                }`}
                              >
                                {option.isPopular && !isDisabled && (
                                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#962831] text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap !text-white">
                                    ⭐ POPULAR
                                  </div>
                                )}
                                <div className="font-bold text-sm mb-1">{option.label}</div>
                                <div className="text-xs text-gray-600">{option.description}</div>
                              </button>

                              {/* Helper text for disabled options */}
                              {isDisabled && (
                                <div className="mt-2 text-xs text-center text-gray-500">
                                  {isBelowMin && 'Below 12-tail minimum'}
                                  {isAboveMax && `Requires ${calculatedTails} tails, exceeding our 400-tail max.`}
                                </div>
                              )}

                              {/* Educational tooltip on click */}
                              {showTooltip && isDisabled && (
                                <div className="absolute top-full left-0 right-0 mt-2 z-10 bg-white border-2 rounded-lg p-4 shadow-lg" style={{ borderColor: '#962831' }}>
                                  <button
                                    onClick={() => setDisabledTooltip(null)}
                                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                                  >
                                    ✕
                                  </button>
                                  <div className="font-bold mb-2 !text-[#962831]">{option.label} Serving Unavailable</div>
                                  <div className="text-sm text-gray-800 mb-2">
                                    {isAboveMax && (
                                      <>
                                        For <strong>{guestCount} guests</strong>, this option requires <strong>{calculatedTails} tails</strong>, which is above our 400-tail maximum for a single event.
                                      </>
                                    )}
                                    {isBelowMin && (
                                      <>
                                        For <strong>{guestCount} guests</strong>, this option requires only <strong>{calculatedTails} tails</strong>, which is below our 12-tail minimum order.
                                      </>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-700">
                                    {isAboveMax && `To select this option, please reduce your guest count to 400 or fewer.`}
                                    {isBelowMin && `To select this option, please increase your guest count or choose a larger serving size.`}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Toggle to Tail Count Mode */}
                    <div className="mb-6 text-center">
                      <button
                        onClick={toggleConfigMode}
                        className="text-[#962831] hover:text-[#962831]/80 font-semibold text-sm underline"
                      >
                        Know exactly how many tails you need? Switch to Tail Count →
                      </button>
                    </div>
                  </>
                )}

                {/* Tail Count Mode */}
                {configMode === 'tails' && (
                  <>
                    <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                      How Many Lobster Tails?
                    </h2>
                    <p className="text-gray-600 mb-6">Enter your desired tail count (12-400) directly.</p>

                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Your Guest Count</span>
                        <span className="text-2xl font-bold !text-[#962831]">
                          {guestCount} guests
                        </span>
                      </div>
                    </div>

                    <div className="mb-8">
                      <label className="block text-sm font-semibold mb-4">Total Number of Tails *</label>
                      <input
                        type="number"
                        min="12"
                        max="400"
                        value={directTailInput}
                        onChange={handleDirectTailChange}
                        className="w-full px-6 py-4 text-2xl border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#962831] focus:border-[#962831]"
                        placeholder="Enter number of tails"
                      />

                      {/* Validation Message */}
                      {validationMessage && (
                        <div className={`mt-4 p-4 rounded-lg border-l-4 ${
                          validationState === 'error'
                            ? 'bg-red-50 border-red-500 text-red-900'
                            : validationState === 'suggestion'
                            ? 'bg-blue-50 border-blue-500 text-blue-900'
                            : 'bg-green-50 border-green-500 text-green-900'
                        }`}>
                          <p className="text-sm font-medium" dangerouslySetInnerHTML={{ __html: validationMessage }}></p>
                        </div>
                      )}

                      {!validationMessage && (
                        <p className="text-sm text-gray-500 mt-2">Enter a number between 12 and 400 tails</p>
                      )}
                    </div>

                    {/* Toggle back to Serving Size Mode */}
                    <div className="mb-6 text-center">
                      <button
                        onClick={toggleConfigMode}
                        className="text-[#962831] hover:text-[#962831]/80 font-semibold text-sm underline"
                      >
                        ← Need a recommendation? Switch back to Serving Size
                      </button>
                    </div>
                  </>
                )}

                {customQuoteRequired ? (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 mb-6">
                    <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                      <span>❌</span> {numTails < 12 ? 'Below Minimum Order' : 'Custom Quote Required'}
                    </h3>
                    {numTails < 12 ? (
                      <>
                        <p className="text-sm text-red-800 mb-3">
                          {configMode === 'serving'
                            ? `This serving size results in ${numTails} tails, but our minimum order is 12 tails.`
                            : `You've entered ${numTails} tails, but our minimum order is 12 tails.`}
                        </p>
                        <p className="text-sm text-red-800">
                          {configMode === 'serving'
                            ? 'Try selecting a larger serving size, or switch to Tail Count mode to order exactly 12 tails.'
                            : 'Please enter a number between 12 and 400 tails.'}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-red-800 mb-3">
                          Your configuration requires {numTails} tails, which exceeds our online maximum of 400 tails.
                        </p>
                        <p className="text-sm text-red-800">
                          Please contact us directly at <a href="mailto:catering@tailtations.com" className="font-semibold underline">catering@tailtations.com</a> for a custom quote.
                        </p>
                      </>
                    )}
                  </div>
                ) : selectedPackage && (
                  <>
                    {/* v9: Service Type Badge */}
                    <div className="mb-4">
                      {serviceType === 'cateringTrays' ? (
                        <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-lg font-bold text-sm">
                          📦 Catering Trays
                        </div>
                      ) : (
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm ${
                          selectedPackage === 'partyStarter' ? 'bg-purple-100 text-purple-800' :
                          selectedPackage === 'corporateFavorite' ? 'bg-blue-100 text-blue-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          🚚 {PACKAGES[selectedPackage].name}
                        </div>
                      )}
                    </div>

                    <div className="bg-blue-50 rounded-lg p-6 mb-6">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-sm font-semibold text-gray-700">Total Tails Required</span>
                        <span className="text-3xl font-bold !text-[#962831]">
                          {numTails} tails
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-gray-600">Price per Tail</span>
                        <span className="text-lg font-bold !text-[#962831]">
                          ${serviceType === 'cateringTrays'
                            ? CATERING_TRAYS[selectedPackage === 'cateringTraysSmall' ? 'small' : 'large'].pricePerTail
                            : PACKAGES[selectedPackage].pricePerTail}
                        </span>
                      </div>
                    </div>

                    {/* v9.1: 79-to-80 Upsell Tooltip (Updated) */}
                    {numTails === 79 && (
                      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6">
                        <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                          <span>✨</span> Upgrade to Full-Service Catering!
                        </h3>
                        <p className="text-sm text-blue-800">
                          Adding just 1 more tail (80 total) unlocks our food truck experience with 2 professional chefs, on-site cooking, and complete setup—all at a better per-tail price!
                        </p>
                      </div>
                    )}

                    {/* v9: Informational Box */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <p className="text-sm text-gray-700">
                        {serviceType === 'cateringTrays'
                          ? '🚚 Catering Trays are delivered ready to serve. Add chafing setup or staff in the next step.'
                          : '🚚 Full-service catering with food truck on-site. Includes 2 chefs and all setup.'}
                      </p>
                    </div>

                    {serviceType === 'foodTruck' && (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-bold mb-3">Included Service</h3>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div>• 2 Professional Chefs</div>
                          <div>• {costs.laborHours} hours total service</div>
                          <div>• Mobile Kitchen Setup</div>
                          <div>• Labor Cost: ${costs.laborCost.toFixed(2)}</div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={customQuoteRequired || (configMode === 'tails' && !canContinue)}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#962831] text-white px-6 py-3 rounded-lg hover:bg-[#962831]/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* v9: Step 3: Conditional Add-Ons */}
            {step === 3 && (
              <div className="pb-12">
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  Optional Add-Ons
                </h2>
                <p className="text-gray-600 mb-6">
                  {serviceType === 'cateringTrays'
                    ? 'Customize your delivery with additional services.'
                    : 'Enhance your event with additional service staff.'}
                </p>

                {serviceType === 'cateringTrays' ? (
                  <div className="space-y-6">
                    {/* v9.2: Delivery Method - Strengthened selected state for mobile */}
                    <div className="border-2 border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-bold mb-4">Delivery Method</h3>
                      <div className="space-y-3">
                        <label className="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-all"
                               style={{
                                 borderColor: deliveryMethod === 'delivery' ? '#962831' : '#e5e7eb',
                                 backgroundColor: deliveryMethod === 'delivery' ? '#fef2f2' : 'transparent',
                                 boxShadow: deliveryMethod === 'delivery' ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'
                               }}>
                          <div>
                            <div className="font-bold">Delivery</div>
                            <div className="text-sm text-gray-600">We deliver to your location (+$50)</div>
                          </div>
                          <input
                            type="radio"
                            name="deliveryMethod"
                            value="delivery"
                            checked={deliveryMethod === 'delivery'}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="w-5 h-5 accent-[#962831]"
                          />
                        </label>
                        <label className="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-all"
                               style={{
                                 borderColor: deliveryMethod === 'pickup' ? '#962831' : '#e5e7eb',
                                 backgroundColor: deliveryMethod === 'pickup' ? '#fef2f2' : 'transparent',
                                 boxShadow: deliveryMethod === 'pickup' ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'
                               }}>
                          <div>
                            <div className="font-bold">Pickup (Free)</div>
                            <div className="text-sm text-gray-600">Pick up from our location</div>
                          </div>
                          <input
                            type="radio"
                            name="deliveryMethod"
                            value="pickup"
                            checked={deliveryMethod === 'pickup'}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="w-5 h-5 accent-[#962831]"
                          />
                        </label>
                      </div>
                    </div>

                    {/* v9.2.1: Chafing Setup - Full card tappable via label wrapper */}
                    <label htmlFor="chafing-toggle" className="block cursor-pointer">
                      <div className="border-2 border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold mb-1">Chafing Setup</h3>
                            <p className="text-sm text-gray-600">Add chafing dishes to keep food warm (+$30)</p>
                          </div>
                          <div className="relative inline-flex items-center">
                            <input
                              type="checkbox"
                              id="chafing-toggle"
                              checked={chafingEnabled}
                              onChange={(e) => setChafingEnabled(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#962831]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#962831]"></div>
                          </div>
                        </div>
                        {chafingEnabled && (
                          <div className="bg-green-50 rounded-lg p-4 flex items-center justify-between mt-4">
                            <span className="text-sm font-semibold text-gray-700">Additional Cost</span>
                            <span className="text-xl font-bold text-green-700">+${CHAFING_FEE.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* v9.2.1: Event Staff - Full card tappable via label wrapper */}
                    <label htmlFor="staff-toggle" className="block cursor-pointer">
                      <div className="border-2 border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold mb-1">Event Staff</h3>
                            <p className="text-sm text-gray-600">Add professional staff to help with your event</p>
                          </div>
                          <div className="relative inline-flex items-centers">
                            <input
                              type="checkbox"
                              id="staff-toggle"
                              checked={staffEnabled}
                              onChange={(e) => setStaffEnabled(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#962831]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#962831]"></div>
                          </div>
                        </div>

                        {staffEnabled && (
                          <div className="space-y-4 pt-4 border-t border-gray-200" onClick={(e) => e.preventDefault()}>
                            <div>
                              <div className="block text-sm font-semibold mb-2">Number of Staff</div>
                              <div className="flex gap-2">
                                {[1, 2, 3, 4].map((num) => (
                                  <button
                                    key={num}
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); setNumStaff(num); }}
                                    className={`flex-1 py-3 rounded-md font-semibold transition-colors ${
                                      numStaff === num
                                        ? 'bg-[#962831] text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {num}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <div className="block text-sm font-semibold mb-2">
                                Service Duration: {staffHours} hours
                              </div>
                              <input
                                type="range"
                                min="1"
                                max="6"
                                step="0.5"
                                value={staffHours}
                                onChange={(e) => setStaffHours(Number(e.target.value))}
                                onClick={(e) => e.preventDefault()}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#962831]"
                              />
                              <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>1 hr</span>
                                <span>6 hrs</span>
                              </div>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-700">Additional Cost</span>
                              <span className="text-xl font-bold text-green-700">
                                +${costs.staffCost.toFixed(2)}
                              </span>
                            </div>

                            <p className="text-xs text-gray-600">
                              ${STAFF_RATE}/hour per staff • {numStaff} staff × {staffHours} hours = ${costs.staffCost.toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                ) : (
                  // v9.2.1: Food Truck: Servers/Runners - Full card tappable via label wrapper
                  <label htmlFor="servers-toggle" className="block cursor-pointer">
                    <div className="border-2 border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold mb-1">Servers/Runners</h3>
                          <p className="text-sm text-gray-600">Professional servers to help manage your event</p>
                        </div>
                        <div className="relative inline-flex items-center">
                          <input
                            type="checkbox"
                            id="servers-toggle"
                            checked={serversEnabled}
                            onChange={(e) => setServersEnabled(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#962831]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#962831]"></div>
                        </div>
                      </div>

                      {serversEnabled && (
                        <div className="space-y-4 pt-4 border-t border-gray-200" onClick={(e) => e.preventDefault()}>
                          <div>
                            <div className="block text-sm font-semibold mb-2">Number of Servers</div>
                            <div className="flex gap-2">
                              {[1, 2, 3, 4].map((num) => (
                                <button
                                  key={num}
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setNumServers(num); }}
                                  className={`flex-1 py-3 rounded-md font-semibold transition-colors ${
                                    numServers === num
                                      ? 'bg-[#962831] text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {num}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <div className="block text-sm font-semibold mb-2">
                              Service Duration: {serverHours} hours
                            </div>
                            <input
                              type="range"
                              min="1"
                              max="6"
                              step="0.5"
                              value={serverHours}
                              onChange={(e) => setServerHours(Number(e.target.value))}
                              onClick={(e) => e.preventDefault()}
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#962831]"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>1 hr</span>
                              <span>6 hrs</span>
                            </div>
                          </div>

                          <div className="bg-green-50 rounded-lg p-4 flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Additional Cost</span>
                            <span className="text-xl font-bold text-green-700">
                              +${costs.serversCost.toFixed(2)}
                            </span>
                          </div>

                          <p className="text-xs text-gray-600">
                            ${SERVER_RATE}/hour per server • {numServers} server(s) × {serverHours} hours = ${costs.serversCost.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#962831] text-white px-6 py-3 rounded-lg hover:bg-[#962831]/90 transition-colors font-semibold"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Contact Information */}
            {step === 4 && (
              <div className="pb-12">
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  Contact Information
                </h2>
                <p className="text-gray-600 mb-6">Tell us about yourself and your event.</p>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-red-700 text-sm">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={contactInfo.fullName}
                        onChange={(e) => setContactInfo({ ...contactInfo, fullName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Email Address *</label>
                      <input
                        type="email"
                        required
                        value={contactInfo.email}
                        onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Phone Number</label>
                      <input
                        type="tel"
                        value={contactInfo.phone}
                        onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Event Date *</label>
                      <input
                        type="date"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        value={contactInfo.eventDate}
                        onChange={(e) => setContactInfo({ ...contactInfo, eventDate: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Event Location (City/Town)</label>
                    <input
                      type="text"
                      value={contactInfo.eventLocation}
                      onChange={(e) => setContactInfo({ ...contactInfo, eventLocation: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                    />
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#962831] text-white px-6 py-3 rounded-lg hover:bg-[#962831]/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit My Configuration'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Right column - Cost Summary (Desktop) / Bottom section (Mobile) */}
          <div className="lg:w-96 bg-gray-50 p-4 md:p-8 lg:sticky lg:top-0 lg:h-fit lg:border-l border-t lg:border-t-0 border-gray-200 mt-8 lg:mt-0">
            <div className="flex items-center gap-2 mb-4">
              <Calculator className="w-5 h-5 !text-[#962831]" />
              <h3 className="text-xl font-bold" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                Cost Summary
              </h3>
            </div>

            {selectedPackage && numTails && !customQuoteRequired ? (
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Tail Cost ({numTails} × ${serviceType === 'cateringTrays'
                          ? CATERING_TRAYS[selectedPackage === 'cateringTraysSmall' ? 'small' : 'large'].pricePerTail
                          : PACKAGES[selectedPackage].pricePerTail})
                      </span>
                      <span className="font-semibold">${costs.tailCost.toFixed(2)}</span>
                    </div>
                    {serviceType === 'cateringTrays' ? (
                      <>
                        {/* v9.1: Always show Delivery Fee in Step 2 for transparency */}
                        {step === 2 || deliveryMethod === 'delivery' ? (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivery Fee{step === 2 ? '*' : ''}</span>
                            <span className="font-semibold">${DELIVERY_FEE.toFixed(2)}</span>
                          </div>
                        ) : null}
                        {chafingEnabled && (
                          <div className="flex justify-between text-green-700">
                            <span>Chafing Setup</span>
                            <span className="font-semibold">+${costs.chafingCost.toFixed(2)}</span>
                          </div>
                        )}
                        {staffEnabled && costs.staffCost > 0 && (
                          <div className="flex justify-between text-green-700">
                            <span>Event Staff ({numStaff} × {staffHours}h)</span>
                            <span className="font-semibold">+${costs.staffCost.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mobile Kitchen Fee</span>
                          <span className="font-semibold">${costs.baseFee.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Labor ({costs.laborHours}h × 2 staff)</span>
                          <span className="font-semibold">${costs.laborCost.toFixed(2)}</span>
                        </div>
                        {serversEnabled && costs.serversCost > 0 && (
                          <div className="flex justify-between text-green-700">
                            <span>Servers ({numServers} × {serverHours}h)</span>
                            <span className="font-semibold">+${costs.serversCost.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="font-semibold">Subtotal</span>
                      <span className="font-semibold">${costs.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Est. Sales Tax (9.5%)</span>
                      <span>${costs.estimatedTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                      <span className="font-bold text-lg">Total</span>
                      <span className="font-bold text-2xl !text-[#962831]">
                        ${costs.totalCost.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-600 italic">
                  * All costs are estimates. Sales tax will be finalized based on your event location.{' '}
                  {serviceType === 'cateringTrays' && step === 2 && 'Delivery Fee shown; free pickup option available in next step. '}
                  Your catering specialist will provide a detailed quote within 24 hours. By submitting this form you have no contractual obligation to purchase.
                </p>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Calculator className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Configure your event details to see your cost breakdown</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CateringWizard
